import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import TrashIconButton from 'app/shared/widgets/TrashIconButton';
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { LoadingButton } from "@mui/lab";
import skuServices from "app/services/sku-services";
import { useSnackbar } from "notistack";

const defaultRetailCodeRow = () => ({ retail_name: "", barcode: "" });

const SKUCreateModal = ({
    open = false,
    onClose = () => {},
    onCreated = () => {},
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [saving, setSaving] = useState(false);
    const [formState, setFormState] = useState({
        sku: "",
        barcode: "",
        last_scanned_at: "",
        first_manual_pick_at: "",
        image: null,
    });
    const [retailCodes, setRetailCodes] = useState([defaultRetailCodeRow()]);

    useEffect(() => {
        if (!open) {
            setFormState({
                sku: "",
                barcode: "",
                last_scanned_at: "",
                first_manual_pick_at: "",
                image: null,
            });
            setRetailCodes([defaultRetailCodeRow()]);
            setSaving(false);
        }
    }, [open]);

    const canSave = useMemo(() => {
        if (saving) return false;

        const hasMainInfo = Boolean(formState.sku?.trim() || formState.barcode?.trim());
        const hasAnyRetailCode = retailCodes.some((item) => item.retail_name?.trim() || item.barcode?.trim());

        return hasMainInfo || hasAnyRetailCode;
    }, [formState, retailCodes, saving]);

    const handleFormFieldChange = (fieldName, fieldValue) => {
        let normalized = fieldValue;
        if (fieldName === "sku" || fieldName === "barcode") normalized = fieldValue.toUpperCase();
        setFormState((prev) => ({ ...prev, [fieldName]: normalized }));
    };

    const handleRetailCodeChange = (index, fieldName, fieldValue) => {
        let normalized = fieldValue;
        if (fieldName === "retail_name" && fieldValue.length > 0) {
            normalized = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
        }
        setRetailCodes((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [fieldName]: normalized };
            return next;
        });
    };

    const addRetailCodeRow = () => {
        setRetailCodes((prev) => [...prev, defaultRetailCodeRow()]);
    };

    const removeRetailCodeRow = (index) => {
        setRetailCodes((prev) => {
            if (prev.length === 1) return prev;
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    };

    const saveSku = async () => {
        if (!canSave) return;

        try {
            setSaving(true);

            await skuServices.createSku({
                sku: formState.sku,
                barcode: formState.barcode,
                last_scanned_at: formState.last_scanned_at || null,
                first_manual_pick_at: formState.first_manual_pick_at || null,
                image: formState.image,
                retail_codes: retailCodes,
            });

            enqueueSnackbar("SKU creado correctamente", { variant: "success" });
            onCreated();
            onClose();
        } catch (_error) {
            // Interceptor handles generic message.
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
            <DialogTitle>
                <Typography variant="h2" color="primary">Crear SKU</Typography>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 0.5 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="SKU"
                                value={formState.sku}
                                onChange={(event) => handleFormFieldChange("sku", event.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Código de barra principal"
                                value={formState.barcode}
                                onChange={(event) => handleFormFieldChange("barcode", event.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="datetime-local"
                                label="Último escaneo"
                                InputLabelProps={{ shrink: true }}
                                value={formState.last_scanned_at}
                                onChange={(event) => handleFormFieldChange("last_scanned_at", event.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="datetime-local"
                                label="Primer pickeo manual"
                                InputLabelProps={{ shrink: true }}
                                value={formState.first_manual_pick_at}
                                onChange={(event) => handleFormFieldChange("first_manual_pick_at", event.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Button variant="outlined" component="label" size="small">
                                    Subir foto
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={(event) => {
                                            const file = event.target.files?.[0] || null;
                                            handleFormFieldChange("image", file);
                                        }}
                                    />
                                </Button>

                                <Typography variant="body2" color="text.secondary">
                                    {formState.image?.name || "Sin archivo seleccionado"}
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                        <Typography variant="h4">Códigos retail</Typography>
                        <Button
                            size="small"
                            startIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={addRetailCodeRow}
                        >
                            Agregar retail
                        </Button>
                    </Stack>

                    {retailCodes.map((retailCode, index) => (
                        <Grid container spacing={2} key={`retail-code-${index}`}>
                            <Grid item xs={12} md={5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Retail"
                                    value={retailCode.retail_name}
                                    onChange={(event) => handleRetailCodeChange(index, "retail_name", event.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Código de barra retail"
                                    value={retailCode.barcode}
                                    onChange={(event) => handleRetailCodeChange(index, "barcode", event.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={1}>
                                <TrashIconButton
                                    onClick={() => removeRetailCodeRow(index)}
                                    disabled={retailCodes.length === 1}
                                />
                            </Grid>
                        </Grid>
                    ))}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
                <LoadingButton
                    loading={saving}
                    variant="contained"
                    color="success"
                    size="small"
                    endIcon={<SaveOutlinedIcon />}
                    disabled={!canSave}
                    loadingPosition="end"
                    onClick={saveSku}
                >
                    {saving ? "Guardando" : "Guardar"}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default SKUCreateModal;
